import { useSyncExternalStore, useEffect } from 'react';

/**
 * Shell-owned notification bridge.
 *
 * Any MFE can push a notification into the shell's bell without importing
 * shell code — they call `window.esgcaffe.notify({...})`. The Header renders
 * these alongside the persistent backend notifications and the PCF-derived
 * notifications. Read/unread state persists per-tenant to localStorage.
 *
 * This complements — does not replace — the backend `/notifications` feed.
 * Backend notifications remain the source of truth for durable, cross-device
 * events. Pushed notifications are for lightweight, module-local signals a
 * running MFE wants to surface immediately (e.g. "recalculation finished").
 */

export interface PushedNotification {
  id: string;
  title: string;
  message?: string;
  href?: string;
  createdAt: string; // ISO
  source: string;   // module key, e.g. 'lca', 'carbon'
  read: boolean;
}

function tenantId(): string {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return 'anon';
    const u = JSON.parse(raw);
    return String(u?.tenantId ?? 'anon');
  } catch {
    return 'anon';
  }
}

function storageKey(): string {
  return `esg.pushedNotifications.${tenantId()}`;
}

interface Persisted {
  items: PushedNotification[];
}

function load(): Persisted {
  if (typeof localStorage === 'undefined') return { items: [] };
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return { items: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return { items: [] };
    return { items: parsed.items.slice(0, 50) };
  } catch {
    return { items: [] };
  }
}

function save(p: Persisted): void {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(p));
  } catch {
    /* quota / private mode — silent */
  }
}

class PushedStore {
  private items: PushedNotification[] = load().items;
  private listeners = new Set<() => void>();

  subscribe = (l: () => void): (() => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  private emit(): void {
    for (const l of this.listeners) l();
    save({ items: this.items });
  }

  getSnapshot = (): readonly PushedNotification[] => this.items;

  push = (n: Omit<PushedNotification, 'read'>): void => {
    // De-dupe by id — pollers often re-fire.
    if (this.items.some((x) => x.id === n.id)) return;
    this.items = [{ ...n, read: false }, ...this.items].slice(0, 50);
    this.emit();
  };

  markRead = (id: string): void => {
    this.items = this.items.map((n) => (n.id === id ? { ...n, read: true } : n));
    this.emit();
  };

  markAllRead = (): void => {
    this.items = this.items.map((n) => ({ ...n, read: true }));
    this.emit();
  };

  clear = (): void => {
    this.items = [];
    this.emit();
  };
}

export const pushedStore = new PushedStore();

export function usePushedNotifications(): {
  items: readonly PushedNotification[];
  unread: number;
  markRead: (id: string) => void;
  markAllRead: () => void;
} {
  const items = useSyncExternalStore(
    pushedStore.subscribe,
    pushedStore.getSnapshot,
    pushedStore.getSnapshot,
  );
  const unread = items.filter((n) => !n.read).length;
  return {
    items,
    unread,
    markRead: pushedStore.markRead,
    markAllRead: pushedStore.markAllRead,
  };
}

/**
 * Attach the global window bridge exactly once. Call from the shell's entry
 * point so every federated MFE sees `window.esgcaffe.notify(...)`.
 */
export function installNotifyBridge(): void {
  if (typeof window === 'undefined') return;
  const w = window as any;
  if (w.__esgNotifyBridgeInstalled) return;
  w.esgcaffe = w.esgcaffe || {};
  w.esgcaffe.notify = (n: Omit<PushedNotification, 'read'>): void => {
    pushedStore.push(n);
  };
  w.__esgNotifyBridgeInstalled = true;
}

/**
 * Hook variant of `installNotifyBridge` for use inside React trees.
 */
export function useInstallNotifyBridge(): void {
  useEffect(() => {
    installNotifyBridge();
  }, []);
}
